import * as React from 'react';
import PropTypes from 'prop-types';
import { uid } from 'react-uid';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import MuiTableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { Stack, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { KeyboardArrowRight, ExpandMore } from '@mui/icons-material';
import StatusBox from './StatusBox';
import FundLogo from './FundLogo';
import TableHead from '../../common/TableHead';
import DistributionsHeadCells from './DistributionsHeadCells';
import PaidDistributionsDetailsRow from './PaidDistributionsDetailsRow';
import {
  getComparator, sortAlphanumerically, sortByDate, sortAmounts,
} from '../../../../shared/hooks/useSort';

function DistributionsTableHead({
  columnsToHide, order, orderBy, onRequestSort, showUnPaidDistributions,
}) {
  const distributionsHeadCells = DistributionsHeadCells(showUnPaidDistributions);
  const cells = columnsToHide
    ? distributionsHeadCells.filter((cell) => !columnsToHide.includes(cell.id))
    : distributionsHeadCells;

  return cells && cells.length > 0 && (
    <TableHead
      headCells={cells}
      order={order}
      orderBy={orderBy}
      onRequestSort={onRequestSort}
    />
  );
}

DistributionsTableHead.propTypes = {
  columnsToHide: PropTypes.arrayOf(PropTypes.string),
  order: PropTypes.string,
  orderBy: PropTypes.string,
  onRequestSort: PropTypes.func,
  showUnPaidDistributions: PropTypes.bool,
};

DistributionsTableHead.defaultProps = {
  columnsToHide: null,
  order: 'asc',
  orderBy: '',
  onRequestSort: () => {},
  showUnPaidDistributions: false,
};

function DistributionsTableHeader({
  label, fundCode, fundTotalDistributions, showUnPaidDistributions = true,
}) {
  return (
    <MuiTableHead>
      <TableRow
        sx={{
          height: '60px',
          pt: 0,
          pb: 0,
        }}
      >
        <TableCell
          key="table-cell1234"
          align="left"
          colSpan={4}
          sx={{ height: '20px', p: 0, verticalAlign: 'baseline' }}
        >
          <Stack
            flexDirection="row"
            alignItems="center"
            height="100%"
            sx={{
              marginLeft: '20px',
            }}
          >
            {showUnPaidDistributions && (
            <FundLogo
              label={label}
              fundCode={fundCode}
              sx={{
                marginRight: '10px',
              }}
            />
            )}
            <span style={{ fontFamily: 'Archivo', fontSize: '20px', fontWeight: '600' }}>{label}</span>
          </Stack>

        </TableCell>
        <TableCell
          key="table-cell12345-total"
          align="right"
          colSpan={1}
          sx={{
            height: '20px', p: 0, verticalAlign: 'center', pr: '16px',
          }}
        >
          <span
            style={{ fontFamily: 'Archivo', fontSize: '14px', fontWeight: '600' }}
          >{`Total: ${fundTotalDistributions}`}
          </span>
        </TableCell>
      </TableRow>
    </MuiTableHead>
  );
}

DistributionsTableHeader.propTypes = {
  label: PropTypes.string.isRequired,
  fundCode: PropTypes.string.isRequired,
  fundTotalDistributions: PropTypes.number.isRequired,
};

const DistributionTableCell = styled(TableCell)({
  fontWeight: 500,
  height: 37,
  paddingTop: 0,
  paddingBottom: 0,
  margin: 0,
});

function TotalRow({ totalAmount }) {
  return (
    <TableRow
      sx={{
        bgcolor: 'table.light',
        '&&:last-child td, &:last-child th': { border: 0 },
      }}
      align="center"
    >
      <TableCell
        align="right"
        colSpan={12}
        sx={{
          height: '42px',
          p: 0,
          pr: '16px',
        }}
      >Total: {totalAmount }
      </TableCell>
    </TableRow>
  );
}

TotalRow.propTypes = {
  totalAmount: PropTypes.string.isRequired,
};

function OtherDistributionsTableHead() {
  return (
    <MuiTableHead>
      <TableRow
        sx={{
          height: '60px',
          pt: 0,
          pb: 0,
        }}
      >
        <TableCell
          key="table-cell1234"
          align="left"
          colSpan={6}
          sx={{ height: '20px', p: 0, verticalAlign: 'baseline' }}
        >
          <Stack
            flexDirection="row"
            alignItems="center"
            height="100%"
          >
            <span style={{
              fontFamily: 'Archivo', fontSize: '20px', fontWeight: '600', marginLeft: '20px',
            }}
            >Other
            </span>
          </Stack>

        </TableCell>
      </TableRow>
    </MuiTableHead>
  );
}

DistributionTableCell.defaultProps = {
  align: 'left',
};

function OtherDistributionsTableRows({ data }) {
  return data.map((item) => (
    <TableRow
      key={`table-row${item.title}`}
      sx={{ bgcolor: '#E6E6E6' }}
      align="center"
    >
      <TableCell
        key={`table-cell${item.title}`}
        align="left"
        colSpan={6}
        sx={{ height: '20px', p: 0, pl: '20px' }}
      >{ item.title }
      </TableCell>
      <TableCell
        key={`table-cell${item.amount}`}
        align="right"
        colSpan={6}
        sx={{ height: '20px', p: 0, pr: '20px' }}
      >{ item.amount }
      </TableCell>
    </TableRow>
  ));
}

OtherDistributionsTableRows.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    amount: PropTypes.string.isRequired,
  })).isRequired,
};

const DistributionDataTableHead = ({
  showOtherDistributions,
  showUnPaidDistributions,
  distributionLabel,
  fundCode,
  distributionDataLength,
  fundTotalDistributions,
}) => {
  // Other table in current distributions page
  if (showOtherDistributions) {
    return <OtherDistributionsTableHead />;
  }

  // Fund tables in current distributions page
  if (showUnPaidDistributions) {
    return (
      <DistributionsTableHeader
        label={distributionLabel}
        fundCode={fundCode}
        fundTotalDistributions={fundTotalDistributions}
      />
    );
  }

  // Show all distributions page
  return (
    <DistributionsTableHeader
      label={distributionLabel}
      fundCode={fundCode}
      fundTotalDistributions={fundTotalDistributions}
      showUnPaidDistributions={false}
    />
  );
};

DistributionDataTableHead.propTypes = {
  showOtherDistributions: PropTypes.bool,
  showUnPaidDistributions: PropTypes.bool,
  distributionLabel: PropTypes.string.isRequired,
  fundCode: PropTypes.string.isRequired,
  distributionDataLength: PropTypes.number.isRequired,
  fundTotalDistributions: PropTypes.number.isRequired,
};

DistributionDataTableHead.defaultProps = {
  showOtherDistributions: false,
  showUnPaidDistributions: false,
};

const DistributionTableRows = ({ distributionData, showUnPaidDistributions }) => {
  const [expandedRows, setExpandedRows] = React.useState([]);

  const toggleRow = (rowId) => {
    setExpandedRows((prev) => (prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]));
  };

  return distributionData.map((row, index) => {
    const rowId = uid(row);
    const isExpanded = expandedRows.includes(rowId);

    return (
      <React.Fragment key={rowId}>
        {/* Main Row */}
        <TableRow
          hover
          tabIndex={-1}
          sx={{
            backgroundColor: index % 2 ? 'table.light' : 'table.main',
            '&&:last-child td, &:last-child th': { border: 0 },
          }}
        >
          {!showUnPaidDistributions && (
          <TableCell>{row.datePaid}</TableCell>
          )}
          {showUnPaidDistributions && (
          <TableCell>{row.companyName}</TableCell>
          )}
          <TableCell>{row.payee}</TableCell>
          <TableCell>{row.paymentMethod}</TableCell>
          <TableCell>
            <Box>
              <StatusBox status={row.status} />
            </Box>
          </TableCell>
          <TableCell align="right">
            {showUnPaidDistributions ? row.amount : row.totalSubdistributions}
            {!showUnPaidDistributions && (
            <IconButton
              onClick={() => toggleRow(rowId)}
              size="small"
            >
              {isExpanded ? <ExpandMore /> : <KeyboardArrowRight />}
            </IconButton>
            )}
          </TableCell>
        </TableRow>

        {/* Expanded Row */}
        {isExpanded && (
        <PaidDistributionsDetailsRow row={row} />
        )}
      </React.Fragment>
    );
  });
};

DistributionTableRows.propTypes = {
  distributionData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      datePaid: PropTypes.string,
      companyName: PropTypes.string,
      payee: PropTypes.string,
      paymentMethod: PropTypes.string,
      status: PropTypes.string,
      amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      details: PropTypes.arrayOf(
        PropTypes.shape({
          fund: PropTypes.string.isRequired,
          portfolioCompany: PropTypes.string.isRequired,
          amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
        }),
      ),
    }),
  ).isRequired,
  showUnPaidDistributions: PropTypes.bool.isRequired,
};

export default function DistributionsTable({
  distributionData,
  distributionLabel,
  showUnPaidDistributions,
  fundCode,
  otherDistributionsData,
  showOtherDistributions,
  fundTotalDistributions,
}) {
  const [order, setOrder] = React.useState(showUnPaidDistributions ? 'asc' : 'desc');
  const [orderBy, setOrderBy] = React.useState(showUnPaidDistributions ? 'companyName' : 'datePaid');

  const handleRequestSort = (event, property) => {
    const orderProperty = property === 'amount' && !showUnPaidDistributions ? 'totalSubdistributions' : property;
    const isAscending = orderBy === orderProperty && order === 'asc';
    setOrder(isAscending ? 'desc' : 'asc');
    setOrderBy(orderProperty);
  };

  const sortedData = React.useMemo(() => {
    if (orderBy === 'datePaid') {
      const distributionSorted = distributionData.sort(getComparator(order, orderBy, sortByDate));
      return distributionSorted;
    }
    if (orderBy === 'amount' || orderBy === 'totalSubdistributions') {
      const distributionSorted = distributionData.sort(
        getComparator(order, orderBy, sortAmounts),
      );
      return distributionSorted;
    }
    const distributionSorted = distributionData.sort(
      getComparator(order, orderBy, sortAlphanumerically),
    );
    return distributionSorted;
  }, [distributionData, order, orderBy]);

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer
        sx={{
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'border.dark',
          borderRadius: 2,
          mb: 4,
          mt: 1.5,
        }}
      >
        <Table
          aria-labelledby="distributions-table"
          sx={{
            minWidth: 750,
          }}
        >
          <DistributionDataTableHead
            showOtherDistributions={showOtherDistributions}
            showUnPaidDistributions={showUnPaidDistributions}
            distributionLabel={distributionLabel}
            fundCode={fundCode}
            distributionDataLength={distributionData.length}
            fundTotalDistributions={fundTotalDistributions}
          />
          <DistributionsTableHead
            columnsToHide={showUnPaidDistributions
              ? ['datePaid', 'issueDate', 'fundName'] : ['companyName', 'fundName', 'issueDate']}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            showUnPaidDistributions={showUnPaidDistributions}
          />
          {showOtherDistributions && <OtherDistributionsTableRows data={otherDistributionsData} />}
          {(!showOtherDistributions && (
          <TableBody>
            <DistributionTableRows
              distributionData={sortedData}
              showUnPaidDistributions={showUnPaidDistributions}
            />
          </TableBody>
          ))}
        </Table>
      </TableContainer>
    </Box>
  );
}

DistributionsTable.propTypes = {
  distributionData: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.shape({
      divider: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    PropTypes.shape({
      companyName: PropTypes.string.isRequired,
      amount: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      payee: PropTypes.string,
      paymentMethod: PropTypes.string.isRequired,
      datePaid: PropTypes.string,
    }),
  ])).isRequired,
  distributionLabel: PropTypes.string,
  showUnPaidDistributions: PropTypes.bool.isRequired,
  fundCode: PropTypes.string,
  otherDistributionsData: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    amount: PropTypes.string.isRequired,
  })),
  showOtherDistributions: PropTypes.bool,
  fundTotalDistributions: PropTypes.string,
};

DistributionsTable.defaultProps = {
  fundCode: null,
  distributionLabel: null,
  otherDistributionsData: [],
  showOtherDistributions: false,
  fundTotalDistributions: '',
};
